import React, { useEffect } from 'react'
import { useSnackbar } from 'notistack';
import { CircularProgress, Box, Button, Typography, Tooltip } from '@mui/material';
import Service from '../Networking/NetworkingUtils';
import { getCookieNoCrypto } from '../Utils/CookieHelper';
import { msClientId } from '../Login/authProvider';
import Paper from '@mui/material/Paper';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

const INVITE_FLOW_STOP_KEY = 'bbverse_invite_flow_stop';
const INVITE_PROMPT_SHOWN_KEY = 'bbverse_invite_prompt_shown';
const PROJECT_STORE_ACCESS_KEY = 'bbverse_project_store_access';

const UserInvitation = () => {

    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = React.useState();
    const [isLoading, setIsLoading] = React.useState(true);
    const [isAcceptedRequest, setIsAcceptedRequest] = React.useState(false);
    const [acceptRequestDisabled, setAcceptRequestDisabled] = React.useState(false);
    const [rejectRequestDisabled, setRejectRequestDisabled] = React.useState(false);
    const [isTableLoading, setIsTableLoading] = React.useState(false);

    const rejectRequest = (row) => {
        setRejectRequestDisabled(true)
        setIsTableLoading(true)
        Service.patch(`/api/app/user/invite/reject_user_invite/?invite_id=${row.id}`, {
            headers: {
                Authorization: getCookieNoCrypto('msal.' + msClientId + '.idtoken'),
            },
        }).then(res => {
            enqueueSnackbar('Request rejected successfully!', {
                variant: 'success'
            })
            setRejectRequestDisabled(false)
            getData()
        }).catch(error => {
            console.log(error)
            enqueueSnackbar('Something went wrong!', {
                variant: 'error'
            })
            setIsTableLoading(false)
            setRejectRequestDisabled(false)
        })
    }

    const getData = () => {
        Service.get('/api/app/user/invite/get_user_pending_invitation_lists/', {
            headers: {
                Authorization: getCookieNoCrypto('msal.' + msClientId + '.idtoken'),
            },
        }).then(res => {
            setData(res.data)
            setIsLoading(false)
            setIsTableLoading(false)
        }).catch(error => {
            console.log(error)
            enqueueSnackbar('Something went wrong!', {
                variant: 'error'
            })
            setIsLoading(false)
        })
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const acceptRequest = (row) => {
        setAcceptRequestDisabled(true)
        setIsTableLoading(true)
        Service.patch(`/api/app/user/invite/accept_user_invite/?invite_id=${row.id}`, {
            headers: {
                Authorization: getCookieNoCrypto('msal.' + msClientId + '.idtoken'),
            },
        }).then(res => {
            enqueueSnackbar('Request accepted successfully!', {
                variant: 'success'
            })
            setIsAcceptedRequest(true)
            setAcceptRequestDisabled(false)
            getData()
        }).catch(error => {
            console.log(error)
            enqueueSnackbar('Something went wrong!', {
                variant: 'error'
            })
            setIsTableLoading(false)
            setAcceptRequestDisabled(false)
        })
    }

    const goToHome = () => {
        sessionStorage.removeItem(INVITE_FLOW_STOP_KEY)
        sessionStorage.removeItem(INVITE_PROMPT_SHOWN_KEY)
        sessionStorage.setItem(PROJECT_STORE_ACCESS_KEY, '1')
        window.location.replace('/store')
    }

    return (
        <div>
            {isLoading ?
                <Box width={'100%'} minHeight='50vh' display='flex' justifyContent='center' alignItems='center'>
                    <CircularProgress />
                </Box> :
                <Box sx={{
                    minHeight: '100vh',
                    py: { xs: 5, sm: 8 },
                    px: 2,
                    background: 'linear-gradient(180deg, #f7f9fc 0%, #eef3fa 100%)'
                }}>
                    <Box>
                        <Box display='flex' justifyContent='center' alignItems='center' color={'white'} >
                            <Box
                                sx={{
                                    width: { xs: '100%', sm: '88%', md: '62%', lg: '50%' },
                                    maxWidth: 720,
                                    backgroundColor: 'primary.main',
                                    borderTopLeftRadius: 2,
                                    borderTopRightRadius: 2,
                                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)'
                                }}
                            >
                                <Box display='flex' justifyContent='space-between' alignItems='center' px={1.5} py={0.75}>
                                    <Typography variant='h6' margin={1} fontWeight={700}>Pending Invitations</Typography>
                                    {isAcceptedRequest ?
                                    <Tooltip title="Go to Home Page">
                                        <Button
                                            onClick={goToHome}
                                            color='secondary'
                                            variant='contained'
                                            aria-label='Go to Home Page'
                                        >
                                           Go to Home
                                        </Button>
                                    </Tooltip> : null}
                                </Box>
                            </Box>
                        </Box>
                        {isTableLoading ? <Box width={'100%'} display='flex' justifyContent='center' alignItems='center'> <CircularProgress /> </Box> :
                            <Box
                                margin={2}
                                display='flex'
                                flexDirection='column'
                                alignItems='center'
                                overflow='auto'
                                minHeight='50vh'
                                maxHeight='60vh'
                            >
                                {data.length > 0 ? data.map((row, index) => (
                                    <Paper
                                        key={index}
                                        elevation={2}
                                        sx={{
                                            width: { xs: '100%', sm: '88%', md: '62%', lg: '50%' },
                                            maxWidth: 720,
                                            overflowX: 'auto',
                                            m: 1,
                                            p: 0.5,
                                            borderRadius: 2,
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: 6
                                            }
                                        }}
                                    >
                                        <Box display='flex' alignItems='center' flexDirection='column' padding={2}>
                                            <Typography variant='subtitle1' fontWeight={700}><b>Project Name:</b> {row.project_name}</Typography>
                                            <Typography sx={{ mt: 0.5 }}><b>Invited By:</b> {row.invited_by_first_name} {row.invites_by_last_name}</Typography>
                                            <Typography color='text.secondary' sx={{ mt: 0.5 }}><b>Expiry Date:</b> {moment(row.expiry_date).format('DD-MM-YYYY')}</Typography>
                                            <Box display='flex' justifyContent='center' marginTop={2} gap={1} flexWrap='wrap'>
                                                <Button disabled={acceptRequestDisabled} size='small' variant="contained" color="success" startIcon={<CheckIcon />} sx={{ mr: 0.5 }} onClick={() => { acceptRequest(row) }}>
                                                    Accept
                                                </Button>
                                                <Button disabled={rejectRequestDisabled} size='small' variant="contained" color="error" startIcon={<CloseIcon />} sx={{ ml: 0.5 }} onClick={() => { rejectRequest(row) }}>
                                                    Reject
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                )) :
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            width: { xs: '100%', sm: '88%', md: '62%', lg: '50%' },
                                            maxWidth: 720,
                                            borderRadius: 2,
                                            p: 3,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography color='text.secondary'>No pending invitations right now.</Typography>
                                    </Paper>}
                            </Box>}
                    </Box>
                </Box>}
        </div>

    )
}

export default UserInvitation;